<template>
    <div class="row">
        <div class="col-md-6 tw-mb-3">
            <label class="form-col-label control-label">{{
                $t("cashTransfer.transferBy")
            }}</label>
            <div class="form-control bg-light tw-flex tw-items-center tw-space-x-2">
                <i class="fas fa-user-tie text-primary"></i>
                <span>{{ transferByName }}</span>
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashTransfer.transferDate")
            }}</label>
            <DatePicker
                v-model="model.transfer_date"
                type="date"
                format="dd-MM-yyyy"
                :placeholder="$t('cashTransfer.transferDate')"
                style="width: 100%"
                :class="{ 'is-invalid': validate.has('transfer_date') }"
            />
            <div class="invalid-feedback" v-if="validate.has('transfer_date')">
                {{ validate.first("transfer_date") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashTransfer.amount")
            }}</label>
            <Input
                v-model="model.transfer_amount"
                type="number"
                :min="0"
                :placeholder="$t('cashTransfer.amount')"
                :class="{ 'is-invalid': validate.has('transfer_amount') }"
            />
            <div class="invalid-feedback" v-if="validate.has('transfer_amount')">
                {{ validate.first("transfer_amount") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashTransfer.currency")
            }}</label>
            <Select
                v-model="model.currency_id"
                :placeholder="$t('cashTransfer.currency')"
                :class="{ 'is-invalid': validate.has('currency_id') }"
            >
                <Option
                    v-for="cur in currencies"
                    :key="cur.currency_id"
                    :value="cur.currency_id"
                >
                    {{ cur.currency_code }} — {{ cur.currency_name_en }}
                </Option>
            </Select>
            <div class="invalid-feedback" v-if="validate.has('currency_id')">
                {{ validate.first("currency_id") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashTransfer.bank")
            }}</label>
            <Select
                v-model="model.bank_id"
                :placeholder="$t('cashTransfer.bank')"
                filterable
                :class="{ 'is-invalid': validate.has('bank_id') }"
                @on-change="onBankChange"
            >
                <Option
                    v-for="bank in banks"
                    :key="bank.bank_id"
                    :value="bank.bank_id"
                    :label="bank.bank_name"
                >
                    {{ bank.bank_name }}
                </Option>
            </Select>
            <div class="invalid-feedback" v-if="validate.has('bank_id')">
                {{ validate.first("bank_id") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashTransfer.bankAccount")
            }}</label>
            <Select
                v-model="selectedAccountId"
                :placeholder="$t('cashTransfer.bankAccount')"
                filterable
                :disabled="!model.bank_id"
                :class="{ 'is-invalid': validate.has('bank_account_no') }"
                @on-change="onAccountChange"
            >
                <Option
                    v-for="acc in filteredAccounts"
                    :key="acc.account_id"
                    :value="acc.account_id"
                    :label="acc.account_no"
                >
                    {{ acc.account_no }} — {{ acc.account_name }}
                </Option>
            </Select>
            <div class="invalid-feedback" v-if="validate.has('bank_account_no')">
                {{ validate.first("bank_account_no") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashTransfer.bankAccountNo")
            }}</label>
            <Input
                v-model="model.bank_account_no"
                :placeholder="$t('cashTransfer.bankAccountNo')"
                :class="{ 'is-invalid': validate.has('bank_account_no') }"
            />
            <div class="invalid-feedback" v-if="validate.has('bank_account_no')">
                {{ validate.first("bank_account_no") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashTransfer.bankAccountName")
            }}</label>
            <Input
                v-model="model.bank_account_name"
                :placeholder="$t('cashTransfer.bankAccountName')"
                :class="{ 'is-invalid': validate.has('bank_account_name') }"
            />
            <div class="invalid-feedback" v-if="validate.has('bank_account_name')">
                {{ validate.first("bank_account_name") }}
            </div>
        </div>

        <div class="col-md-12 tw-mb-3">
            <label class="form-col-label control-label">{{
                $t("cashTransfer.remark")
            }}</label>
            <textarea
                v-model.trim="model.remark"
                rows="2"
                class="form-control"
                :placeholder="$t('cashTransfer.remark')"
            ></textarea>
        </div>
    </div>
</template>

<script>
export default {
    name: "CashTransferFormInput",
    props: {
        value: { type: Object, required: true },
        validate: { type: Object, required: true },
        transferByName: { type: String, default: "" },
        currencies: { type: Array, default: () => [] },
        banks: { type: Array, default: () => [] }
    },
    data() {
        return {
            selectedAccountId: null
        };
    },
    computed: {
        model() {
            return this.value;
        },
        filteredAccounts() {
            if (!this.model.bank_id) return [];
            const bank = this.banks.find(b => b.bank_id === this.model.bank_id);
            return bank ? (bank.accounts || []) : [];
        }
    },
    methods: {
        onBankChange() {
            this.selectedAccountId = null;
            this.model.bank_account_no = "";
            this.model.bank_account_name = "";
        },
        onAccountChange(accountId) {
            const acc = this.filteredAccounts.find(a => a.account_id === accountId);
            if (acc) {
                this.model.bank_account_no = acc.account_no;
                this.model.bank_account_name = acc.account_name;
            }
        }
    }
};
</script>
